// Year Overview — the whole year (W1–W52) at a glance for one hub or the network:
// a KPI strip, a forecast-vs-actual area/line chart, a weekly trucks bar, an
// event/reroute marker strip (shared time axis), and a compact monthly table.
// Pure SVG (no chart dependency); all numbers come from the forecast engine +
// customer_actuals. Drill into the Yearly table for week/day detail.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import {
  forecastCustomerHub,
  impliedLoads,
  reroutedInToHub,
  reroutedOutOfHub,
} from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import {
  buildOverrideMap,
  listAccountOverrides,
  type ForecastOverride,
} from "./overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface WeekAgg {
  index: number; // 1..52
  start: string;
  monthIdx: number; // 0..11 (month of the week's start)
  forecast: number;
  actual: number;
  hasActual: boolean;
  loads: number;
  rerouteIn: number;
  rerouteOut: number;
  hasEvent: boolean;
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function YearOverviewPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const nowYear = Number(today().slice(0, 4));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [hubId, setHubId] = useState(""); // "" = all hubs
  const [year, setYear] = useState(nowYear);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = [nowYear - 1, nowYear, nowYear + 1];

  const weeks = useMemo(() => {
    let w = startOfWeekSat(`${year}-01-01`);
    const out: Array<{ index: number; start: string; days: string[] }> = [];
    for (let i = 0; i < 52; i++) {
      out.push({ index: i + 1, start: w, days: Array.from({ length: 7 }, (_, d) => addDays(w, d)) });
      w = addDays(w, 7);
    }
    return out;
  }, [year]);
  const rangeStart = weeks[0]?.start ?? `${year}-01-01`;
  const rangeEnd = weeks[weeks.length - 1]?.days[6] ?? `${year}-12-31`;

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountCustomerActuals(accountId, rangeStart, rangeEnd),
    ])
      .then(([c, h, ln, ov, ev, ac]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setActuals(ac);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load year overview.")));
  }, [accountId, rangeStart, rangeEnd]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);
  const scopeHubs = useMemo(() => (hubId ? hubs.filter((h) => h.id === hubId) : hubs), [hubs, hubId]);

  const actualByHubDay = useMemo(() => {
    const m = new Map<string, number>(); // `${hubId}|${date}` -> total
    for (const a of actuals) m.set(`${a.hubId}|${a.date}`, (m.get(`${a.hubId}|${a.date}`) ?? 0) + a.actualVolume);
    return m;
  }, [actuals]);

  const allDays = useMemo(() => weeks.flatMap((w) => w.days), [weeks]);

  // Per-day totals across the scope — the single source the chart (weekly) and the
  // monthly table (by calendar day) both derive from.
  interface DayAgg {
    forecast: number;
    actual: number;
    hasActual: boolean;
    loads: number;
    rin: number;
    rout: number;
  }
  const perDay = useMemo(() => {
    const m = new Map<string, DayAgg>();
    for (const d of allDays) {
      let forecast = 0;
      let actual = 0;
      let hasActual = false;
      let loads = 0;
      let rin = 0;
      let rout = 0;
      for (const h of scopeHubs) {
        for (const c of customers) {
          const v = forecastCustomerHub(c, h.id, d, overrideMap, events);
          if (v > 0) {
            forecast += v;
            loads += impliedLoads(v, capByKey.get(`${c.id}|${h.id}`));
          }
        }
        const a = actualByHubDay.get(`${h.id}|${d}`);
        if (a != null) {
          actual += a;
          hasActual = true;
        }
        if (events.length) {
          rin += reroutedInToHub(customers, h.id, d, events);
          rout += reroutedOutOfHub(customers, h.id, d, events);
        }
      }
      m.set(d, { forecast, actual, hasActual, loads, rin, rout });
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDays, scopeHubs, customers, overrideMap, events, capByKey, actualByHubDay]);

  const data = useMemo<WeekAgg[]>(() => {
    return weeks.map((w) => {
      let forecast = 0;
      let actual = 0;
      let hasActual = false;
      let loads = 0;
      let rerouteIn = 0;
      let rerouteOut = 0;
      for (const d of w.days) {
        const pd = perDay.get(d);
        if (!pd) continue;
        forecast += pd.forecast;
        actual += pd.actual;
        loads += pd.loads;
        rerouteIn += pd.rin;
        rerouteOut += pd.rout;
        if (pd.hasActual) hasActual = true;
      }
      const hasEvent = events.length
        ? events.some(
            (e) =>
              e.active !== false &&
              scopeHubs.some((h) => h.id === e.hubId) &&
              e.startDate <= w.days[6] &&
              e.endDate >= w.start
          )
        : false;
      return {
        index: w.index,
        start: w.start,
        monthIdx: new Date(w.start + "T12:00:00").getMonth(),
        forecast,
        actual,
        hasActual,
        loads,
        rerouteIn,
        rerouteOut,
        hasEvent,
      };
    });
  }, [weeks, perDay, events, scopeHubs]);

  // KPIs
  const kpi = useMemo(() => {
    let f = 0;
    let a = 0;
    let loads = 0;
    let peak = { forecast: 0, index: 0 };
    const pairs: Array<{ forecast: number; actual: number }> = [];
    for (const w of data) {
      f += w.forecast;
      a += w.actual;
      loads += w.loads;
      if (w.forecast > peak.forecast) peak = { forecast: w.forecast, index: w.index };
      if (w.hasActual) pairs.push({ forecast: w.forecast, actual: w.actual });
    }
    let acc: number | null = null;
    if (pairs.length) {
      const mape =
        (pairs.reduce((s, p) => s + (p.actual > 0 ? Math.abs(p.forecast - p.actual) / p.actual : 0), 0) /
          pairs.length) *
        100;
      acc = Math.max(0, 100 - mape);
    }
    return { f, a, loads, peak, acc };
  }, [data]);

  // Monthly rollup — bucketed by each day's calendar month (so month-boundary
  // weeks split correctly and match the Yearly month view + raw data).
  const months = useMemo(() => {
    const m = Array.from({ length: 12 }, () => ({ forecast: 0, actual: 0, loads: 0, hasActual: false }));
    for (const [d, pd] of perDay) {
      const mo = m[Number(d.slice(5, 7)) - 1];
      mo.forecast += pd.forecast;
      mo.actual += pd.actual;
      mo.loads += pd.loads;
      if (pd.hasActual) mo.hasActual = true;
    }
    return m;
  }, [perDay]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  // ---- SVG chart geometry (shared x axis across the forecast chart + trucks bar) ----
  const W = 1040;
  const padL = 6;
  const padR = 6;
  const n = data.length; // 52
  const xAt = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const yMax = Math.max(1, ...data.map((w) => Math.max(w.forecast, w.actual)));
  const CH = 200; // forecast chart height
  const cTop = 8;
  const cBot = CH - 16;
  const yAt = (v: number) => cBot - (v / yMax) * (cBot - cTop);

  const fcLine = data.map((w, i) => `${xAt(i).toFixed(1)},${yAt(w.forecast).toFixed(1)}`).join(" ");
  const fcArea = `M ${xAt(0).toFixed(1)},${cBot} L ${fcLine} L ${xAt(n - 1).toFixed(1)},${cBot} Z`;
  const actualPts = data
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => w.hasActual)
    .map(({ w, i }) => ({ x: xAt(i), y: yAt(w.actual) }));
  const actualLine = actualPts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // Trucks bar geometry
  const TH = 90;
  const loadMax = Math.max(1, ...data.map((w) => w.loads));
  const barW = ((W - padL - padR) / n) * 0.7;

  // Month label positions: first week whose month differs from the previous.
  const monthLabels = data
    .map((w, i) => ({ w, i }))
    .filter(({ w, i }) => i === 0 || data[i - 1].monthIdx !== w.monthIdx)
    .map(({ w, i }) => ({ x: xAt(i), label: MONTHS[w.monthIdx] }));

  const scopeLabel = hubId ? hubs.find((h) => h.id === hubId)?.name ?? "hub" : "all hubs";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Year Overview</h1>
        <Link to="/yearly" className="text-sm font-medium text-brand-600 hover:text-brand-700">
          Week/day detail →
        </Link>
      </div>
      <p className="text-xs text-slate-500">
        The whole year at a glance for {scopeLabel} — forecast vs actual, trucks, and events. Drill
        into the Yearly table for week-by-week detail.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Hub">
            <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
              <option value="">All hubs</option>
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Year">
            <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label={`Forecast · ${year}`} value={fmtN(kpi.f)} sub="packages" />
        <Stat label={`Actual · ${year}`} value={fmtN(kpi.a)} sub="recorded" />
        <Stat label="Accuracy" value={kpi.acc == null ? "—" : `${kpi.acc.toFixed(1)}%`} sub="100 − MAPE" />
        <Stat label="Loads" value={fmtN(kpi.loads)} sub="implied trucks" />
        <Stat
          label="Peak week"
          value={kpi.peak.index ? `W${kpi.peak.index}` : "—"}
          sub={kpi.peak.index ? `${fmtN(kpi.peak.forecast)} pkg` : ""}
        />
      </div>

      {/* Forecast vs actual chart */}
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Forecast vs actual (weekly)</span>
          <span className="flex items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-3 rounded-sm bg-brand-300" /> Forecast
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-3 rounded-sm bg-slate-800" /> Actual
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rotate-45 bg-amber-500" /> Event/reroute
            </span>
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${CH + 14}`} className="w-full" preserveAspectRatio="none">
          {/* month gridlines + labels */}
          {monthLabels.map((m, i) => (
            <g key={i}>
              <line x1={m.x} y1={cTop} x2={m.x} y2={cBot} className="stroke-slate-100" strokeWidth={1} />
              <text x={m.x + 2} y={CH + 10} className="fill-slate-400" fontSize={11}>
                {m.label}
              </text>
            </g>
          ))}
          {/* forecast area + line */}
          <path d={fcArea} className="fill-brand-100" opacity={0.7} />
          <polyline points={fcLine} className="fill-none stroke-brand-500" strokeWidth={1.5} />
          {/* actual line + dots */}
          {actualPts.length > 1 && (
            <polyline points={actualLine} className="fill-none stroke-slate-800" strokeWidth={1.5} />
          )}
          {actualPts.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={2} className="fill-slate-800" />
          ))}
          {/* event / reroute markers along the baseline */}
          {data.map((w, i) =>
            w.hasEvent || w.rerouteIn > 0 || w.rerouteOut > 0 ? (
              <rect
                key={i}
                x={xAt(i) - 3}
                y={cBot + 1}
                width={6}
                height={6}
                className="fill-amber-500"
                transform={`rotate(45 ${xAt(i)} ${cBot + 4})`}
              >
                <title>
                  {`W${w.index}: ${w.hasEvent ? "hub event" : ""}${
                    w.rerouteIn > 0 ? ` +${fmtN(w.rerouteIn)} rerouted in` : ""
                  }${w.rerouteOut > 0 ? ` −${fmtN(w.rerouteOut)} rerouted out` : ""}`}
                </title>
              </rect>
            ) : null
          )}
        </svg>
      </Card>

      {/* Trucks bar */}
      <Card>
        <div className="mb-2 text-sm font-medium text-slate-700">Trucks / loads (weekly)</div>
        <svg viewBox={`0 0 ${W} ${TH + 14}`} className="w-full" preserveAspectRatio="none">
          {monthLabels.map((m, i) => (
            <text key={i} x={m.x + 2} y={TH + 10} className="fill-slate-400" fontSize={11}>
              {m.label}
            </text>
          ))}
          {data.map((w, i) => {
            const h = (w.loads / loadMax) * (TH - 6);
            return (
              <rect
                key={i}
                x={xAt(i) - barW / 2}
                y={TH - 4 - h}
                width={barW}
                height={Math.max(0, h)}
                className="fill-slate-300"
              >
                <title>{`W${w.index}: ${fmtN(w.loads)} loads`}</title>
              </rect>
            );
          })}
        </svg>
      </Card>

      {/* Monthly summary */}
      <Card className="overflow-x-auto p-0">
        <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
          Monthly summary
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-2 font-medium">Month</th>
              <th className="px-3 py-2 text-right font-medium">Forecast</th>
              <th className="px-3 py-2 text-right font-medium">Actual</th>
              <th className="px-3 py-2 text-right font-medium">Variance</th>
              <th className="px-5 py-2 text-right font-medium">Loads</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m, i) => {
              const v = m.actual - m.forecast;
              return (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-1.5 font-medium text-slate-900">{MONTHS[i]}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(m.forecast)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                    {m.hasActual ? fmtN(m.actual) : "—"}
                  </td>
                  <td
                    className={`px-3 py-1.5 text-right tabular-nums ${
                      !m.hasActual ? "text-slate-300" : v < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {!m.hasActual ? "—" : `${v > 0 ? "+" : ""}${fmtN(v)}`}
                  </td>
                  <td className="px-5 py-1.5 text-right tabular-nums text-slate-600">{fmtN(m.loads)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
              <td className="px-5 py-2 text-slate-900">Year</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-900">{fmtN(kpi.f)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-900">{fmtN(kpi.a)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                {`${kpi.a - kpi.f > 0 ? "+" : ""}${fmtN(kpi.a - kpi.f)}`}
              </td>
              <td className="px-5 py-2 text-right tabular-nums text-slate-900">{fmtN(kpi.loads)}</td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}
